/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/property_files-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-property_files-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/property_files-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, property_id, data_source_id, uploaded_by_user_profile_id, archived_by_user_profile_id, archive_batch_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/property_files-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-property_files-api-client.ts (API client)
 * - client/api/generated_schemas/property_files.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/property_files-entity-config.ts
 * Public fields: 15/15 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PropertyFilesClient, PropertyFilesQueryOptions } from '../generated_clients/generated-property_files-api-client.js';
import {
  PropertyFilesInsert,
  PropertyFilesUpdate,
  PropertyFilesFieldConfig
} from '../../types/generated_schemas/generated-property_files-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/property_files_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const propertyFilesKeys = {
  all: ['property_files'] as const,
  lists: () => [...propertyFilesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<PropertyFilesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...propertyFilesKeys.lists(), { filters, ...page }] as const,
  details: () => [...propertyFilesKeys.all, 'detail'] as const,
  detail: (id: string) => [...propertyFilesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...propertyFilesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...propertyFilesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...propertyFilesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...propertyFilesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const usePropertyFilesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new PropertyFilesClient();
};

/**
 * Hook to get all property_files with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with property_files data
 */
export const usePropertyFilesList = (options?: PropertyFilesQueryOptions) => {
  const client = usePropertyFilesClient();
  
  return useQuery({
    queryKey: propertyFilesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single property_file by ID
 *
 * @param id - The property_file ID
 * @returns React Query result with single property_file data
 */
export const usePropertyFiles = (id: string) => {
  const client = usePropertyFilesClient();
  
  return useQuery({
    queryKey: propertyFilesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/property_files
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/property_files_business_hooks/
 */

/**
 * Hook to create a new property_file
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating property_file
 */
export const useCreatePropertyFiles = () => {
  const client = usePropertyFilesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PropertyFilesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertyFilesKeys.all });
    },
  });
};

/**
 * Hook to update an existing property_file
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating property_file
 */
export const useUpdatePropertyFiles = () => {
  const client = usePropertyFilesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PropertyFilesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: propertyFilesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: propertyFilesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a property_file
 *
 * @returns React Query mutation for deleting property_file
 */
export const useDeletePropertyFiles = () => {
  const client = usePropertyFilesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: propertyFilesKeys.all });
    },
  });
};

/**
 * Hook to count property_files with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, property_id, data_source_id, uploaded_by_user_profile_id, archived_by_user_profile_id, archive_batch_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const usePropertyFilesCount = (filter?: Partial<Record<typeof PropertyFilesFieldConfig.filterable[number], any>>) => {
  const client = usePropertyFilesClient();
  
  return useQuery({
    queryKey: propertyFilesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const usePropertyFilesFieldConfig = () => {
  return PropertyFilesFieldConfig;
};